import React, { useEffect, useState } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { BasePageContent } from '../BasePage';
import { useEditEventContext } from '../../contexts/EditEventContext';
import { endUserEvent, startUserEvent, UserEventType } from '../../lib/performance';
import { ColorScheme, useBackgroundContext } from '../../contexts/BackgroundContext';
import { useEventCacheContext } from '../../contexts/EventCacheContext';
import { TAppElkAttendeeRole } from 'TProtocol/prototypes/events/messages';
import { useUserContext } from '../../contexts/UserContext';
import BasePageHeader from '../BasePageHeader';
import { parseName } from '../../util/attendee';
import EventRow from './EventRow';
import { IUEvent } from '../../lib/event';
import FooterLinks from '../../components/FooterLinks';

enum EventsTab {
  All = 1,
  FromMe
}

interface EventsPageState {
  activeTab: EventsTab;
}

const EventsPage = () => {
  const navigate = useNavigate();
  const location = useLocation() as Location<EventsPageState>;
  const eventContext = useEditEventContext();
  const userContext = useUserContext();

  const eventCacheContext = useEventCacheContext();
  const backgroundContext = useBackgroundContext();
  const [events, setEvents] = useState<IUEvent[]>([]);

  const activeTab = (location.state?.activeTab as EventsTab) || EventsTab.All;

  useEffect(() => {
    document.title = 'Shine - My Events';

    backgroundContext.setScheme({ scheme: ColorScheme.Welcome });
    void loadEvents();
    eventContext.clearEvent();
  }, []);

  useEffect(() => {
    if (eventCacheContext.events !== undefined) {
      endUserEvent(userContext);
    }
  }, [eventCacheContext.events]);

  useEffect(() => {
    const isHost = (event: IUEvent) => event.attendees.find(
      attendee => attendee.role === TAppElkAttendeeRole.ORGANIZER && attendee.userId === userContext.id
    );
    const isNotHost = (event: IUEvent) => event.attendees.find(
      attendee => attendee.role !== TAppElkAttendeeRole.ORGANIZER || attendee.userId !== userContext.id
    );

    let eventsToShow: IUEvent[];
    if (eventCacheContext.events === undefined) {
      eventsToShow = [];
    } else if (activeTab === EventsTab.FromMe) {
      eventsToShow = eventCacheContext.events?.filter(isHost);
    } else {
      eventsToShow = eventCacheContext.events;
    }
    eventsToShow = eventsToShow.sort(sortEvents);

    setEvents(eventsToShow);
  }, [activeTab, eventCacheContext.events]);

  const setActiveTab = (tab: EventsTab) => {
    navigate('/events', { state: { activeTab: tab }, replace: true });
  };

  const loadEvents = async () => {
    await eventCacheContext.fetchEvents();
  };

  const onTap = (event: IUEvent) => {
    startUserEvent(UserEventType.LoadEventFromEvents);
    navigate('/event/' + event.id);
  };

  const sortEvents = (a: IUEvent, b: IUEvent) => b.startTime - a.startTime;

  const renderEvents = () => {
    if (events.length > 0) {
      return events.map(event => (
        <EventRow key={event.id} event={event} showAttendees={true} onClick={() => onTap(event)}/>
      ));
    } else if (eventCacheContext.events !== undefined) {
      return null;
    } else {
      return [2, 3, 4, 5, 6, 7, 8].map(
        (i) => <EventRow showAttendees={true} key={i} onClick={() => null}/>
      );
    }
  };

  return (
    <>
      <BasePageHeader/>
      <BasePageContent $showFooter={true} $wide={true} $full={true}>
        <TopContent>
          <TabContainer>
            <Tab $active={activeTab === EventsTab.All} onClick={() => setActiveTab(EventsTab.All)}>All Events</Tab>
            <Tab $active={activeTab === EventsTab.FromMe} onClick={() => setActiveTab(EventsTab.FromMe)}>Hosted By You</Tab>
          </TabContainer>
        </TopContent>
        <EventsContainer>
          {renderEvents()}
          {eventCacheContext.events !== undefined ?
            <EventRow key="create" event={undefined} showAttendees={false} onClick={() => null} showCreate={true}
                      showPlus={events.length > 0}/> : null}
        </EventsContainer>
        <FooterLinks/>
      </BasePageContent>
    </>
  );
};


const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px 0;
`;

const Tab = styled.div<{ $active: boolean }>`
  display: flex;
  padding: 8px 12px;
  cursor: pointer;
  border: ${props => (props.$active ? '0px' : '0px')};;
  border-radius: 20px;
  font-family: "Poppins", sans-serif;
  color: ${props => (props.$active ? 'var(--shine-system-button-text-color)' : 'var(--shine-text-color)')};;
  font-size: 15px;
  margin-right: 10px;
  background-color: ${props => (props.$active ? 'var(--shine-highlight-color)' : 'rgb(235, 235, 235, 0)')};)}
;
`;

const ButtonContainer = styled.div`
  width: 200px;
`;

const EventsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const TopContent = styled.div`
  /*  position: sticky; !* or fixed *!
    top: 0;
    z-index: 10; !* To ensure it stays above other content *!
    background-color: var(--shine-bg-color);*/
`;

export default EventsPage;

